
import { supabase } from "@/integrations/supabase/client";

interface ArticleBlock {
  id?: string;
  article_id: string;
  type: string;
  content: any;
  position: number;
}

export const articleBlockService = {
  async getBlocksByArticleId(articleId: string) {
    try {
      const { data, error } = await supabase
        .from('article_blocks')
        .select('*')
        .eq('article_id', articleId)
        .order('position');
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching article blocks:', error);
      return { data: null, error };
    }
  },
  
  async createBlock(block: ArticleBlock) {
    try {
      const { data, error } = await supabase
        .from('article_blocks')
        .insert(block)
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Error creating article block:', error);
      return { data: null, error };
    }
  },
  
  async updateBlock(id: string, changes: Partial<ArticleBlock>) {
    try {
      const { data, error } = await supabase
        .from('article_blocks')
        .update({
          ...changes,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Error updating article block:', error);
      return { data: null, error };
    }
  },
  
  async deleteBlock(id: string) {
    try {
      const { error } = await supabase
        .from('article_blocks')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      console.error('Error deleting article block:', error);
      return { error };
    }
  },
  
  async deleteBlocksByArticleId(articleId: string) {
    try {
      const { error } = await supabase
        .from('article_blocks')
        .delete()
        .eq('article_id', articleId);
      
      if (error) throw error;
      
      return { error: null };
    } catch (error) {
      console.error('Error deleting article blocks:', error);
      return { error };
    }
  },
  
  async createBlocksFromEditorData(articleId: string, editorData: any) {
    try {
      if (!editorData || !editorData.blocks || !editorData.blocks.length) {
        return { data: [], error: null };
      }
      
      // Delete existing blocks for this article
      const { error: deleteError } = await this.deleteBlocksByArticleId(articleId);
      if (deleteError) return { data: null, error: deleteError };
      
      // Create blocks from editor data
      const blocks = editorData.blocks.map((block: any, index: number) => ({
        article_id: articleId,
        type: block.type,
        content: block.data,
        position: index
      }));
      
      // Handle empty blocks array case
      if (blocks.length === 0) {
        return { data: [], error: null };
      }
      
      const { data, error } = await supabase
        .from('article_blocks')
        .insert(blocks)
        .select();
      
      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Error creating blocks from editor data:', error);
      return { data: null, error };
    }
  },
  
  async reorderBlocks(articleId: string, blockIds: string[]) {
    try {
      // Update each block's position based on the order of IDs
      for (let i = 0; i < blockIds.length; i++) {
        await supabase
          .from('article_blocks')
          .update({ position: i })
          .eq('id', blockIds[i])
          .eq('article_id', articleId);
      }
      
      return { error: null };
    } catch (error) {
      console.error('Error reordering blocks:', error);
      return { error };
    }
  },
  
  async convertEditorJSToBlocks(articleId: string, editorData: any) {
    try {
      if (!editorData || !editorData.blocks) {
        return { data: [], error: null };
      }
      
      const blocks = editorData.blocks.map((block: any, index: number) => {
        let blockType = block.type;
        let blockContent = block.data;
        
        // Transform specific block types if needed
        if (blockType === 'header') {
          blockType = 'heading';
          blockContent = {
            text: block.data.text,
            level: block.data.level || 2
          };
        }
        
        return {
          article_id: articleId,
          type: blockType,
          content: blockContent,
          position: index
        };
      });
      
      return { data: blocks, error: null };
    } catch (error) {
      console.error('Error converting EditorJS data to blocks:', error);
      return { data: null, error };
    }
  },
  
  async convertBlocksToEditorJS(blocks: any[]) {
    try {
      if (!blocks || !blocks.length) {
        return { 
          version: "1.0", 
          blocks: [] 
        };
      }
      
      // Sort blocks by position
      const sortedBlocks = [...blocks].sort((a, b) => a.position - b.position);
      
      // Convert to EditorJS format
      const editorBlocks = sortedBlocks.map(block => {
        let blockType = block.type;
        let blockData = block.content;
        
        // Transform specific block types if needed
        if (blockType === 'heading') {
          blockType = 'header';
          blockData = {
            text: block.content.text,
            level: block.content.level || 2
          };
        }
        
        return {
          type: blockType,
          data: blockData
        };
      });
      
      return { 
        version: "1.0", 
        blocks: editorBlocks 
      };
    } catch (error) {
      console.error('Error converting blocks to EditorJS data:', error);
      return { 
        version: "1.0", 
        blocks: [] 
      };
    }
  }
};
